import { createSelector } from 'reselect'
import { toSafeObject, toSafeArray } from '../../store/helpers/StateHelper'
import { normalizeMaterialCode, sparePartCodeKey } from '../../store/helpers/StringHelper'
import { servicesEquipmentsSelector } from '../services/servicesSelectors'
import { substituteMaterialsIndexSelector } from '../substituteMaterials/substituteMaterialsSelectors'

/**
 * Get the spare part request opened
 */
export const sparePartIndexSelector = createSelector(
	(state) => state.spareParts.get('index'),
	(requestMap) => {
		return toSafeObject(requestMap)
	}
)

/**
 * Selector to get the selected service when creating a spare part request
 */
export const selectedServiceSelector = createSelector(
	(state) => state.spareParts.get('create').get('service'),
	(selectedService) => {
		return toSafeObject(selectedService)
	}
)

/**
 * Selector to get the equipments in the selected service when creating the spare part request
 */
export const equipmentsInServiceSelector = createSelector(selectedServiceSelector, servicesEquipmentsSelector, (selectedService, allEquipments) => {
	if (!selectedService) return []
	else {
		return allEquipments.filter((srv) => {
			return srv.Ticket === selectedService.Ticket
		})
	}
})

/**
 * Get the spare part request opened
 */
export const sparePartCreateSelector = createSelector(
	(state) => state.spareParts.get('create'),
	equipmentsInServiceSelector,
	(createRequestMap, equipments) => {
		let createRequest = toSafeObject(createRequestMap)

		// Define all the equipments included in the create view
		// from the list of equipment is the selected service.
		// This allows to show a list of equipments when the draft is null
		createRequest.equipments = equipments.map((equip) => {
			equip.spareParts = createRequest.draft ? createRequest.draft.SpareParts.filter((sp) => sp.Equipment.Serie === equip.Serie) : []
			return equip
		})
		return createRequest
	}
)

/**
 * Get the spare part request opened
 */
export const sparePartShowSelector = createSelector(
	(state) => state.spareParts.get('show'),
	(showRequestMap) => {
		let showRequest = toSafeObject(showRequestMap)
		// Define all the equipments included in the create view
		if (showRequest.open) {
			showRequest.equipments = showRequest.open.Equipments.map((equip) => {
				equip.spareParts = showRequest.open ? showRequest.open.SpareParts.filter((sp) => sp.Equipment.Serie === equip.Serie) : []
				return equip
			})
		}

		return showRequest
	}
)

/**
 * Reduce a request's spare parts to the set of codes it already contains, keyed by equipment.
 *
 * The equipment matters. A request can span several equipments, and the row's identity is the pair
 * of code and serie — two coolers that need the same replacement part are two legitimate rows, not
 * a duplicate. Keying by code alone would make the pre-check refuse the second equipment's part and
 * report it as already present, and the server (which is serie-aware) would have accepted it.
 *
 * Derived from the request itself rather than tracked separately, which is what makes parts that
 * arrived through an earlier substitution count the same as parts the user added directly: once the
 * server returns the updated request, both kinds are just rows.
 *
 * Normalizing with the same helper used to correlate against the catalog is what makes two codes
 * that differ only in whitespace or leading zeros register as the same material.
 * @param {*} request
 */
export const getSparePartCodesInRequest = (request) => {
	let codes = {}
	if (!request || !request.SpareParts) return codes

	request.SpareParts.forEach((sparePart) => {
		let normalized = normalizeMaterialCode(sparePart ? sparePart.Code : '')
		if (normalized.length > 0) {
			let serie = sparePart && sparePart.Equipment ? sparePart.Equipment.Serie : ''
			codes[sparePartCodeKey(serie, normalized)] = true
		}
	})
	return codes
}

/**
 * Codes already present in the draft request (create flow).
 */
export const sparePartCodesInDraftSelector = createSelector(
	(state) => state.spareParts.getIn(['create', 'draft']),
	(draftMap) => {
		return getSparePartCodesInRequest(draftMap ? toSafeObject(draftMap) : null)
	}
)

/**
 * Codes already present in the open manual request (open flow).
 *
 * Deliberately separate from the draft selector rather than a union of both: a stale draft left in
 * the store from an earlier create would otherwise make the open flow report materials as already
 * present when they are not.
 */
export const sparePartCodesInOpenRequestSelector = createSelector(
	(state) => state.spareParts.getIn(['show', 'open']),
	(openMap) => {
		return getSparePartCodesInRequest(openMap ? toSafeObject(openMap) : null)
	}
)

/**
 * Decorate each spare part with its discontinued status from the substitutable materials catalog.
 *
 * Correlation is by the spare part's CODE, never by its SAP material code, and both sides are
 * normalized so formatting differences (whitespace, leading zeros) do not hide a substitution.
 * A part absent from the catalog gets `IsDiscontinued: false` and `Substitute: null`, so callers
 * can tell "not discontinued" from "unknown" without inspecting the catalog themselves.
 *
 * An empty catalog leaves every row flagged as current, which is what keeps the listing behaving
 * exactly as it did before this capability existed.
 * @param {*} spareParts
 * @param {*} substitutesIndex
 */
const decorateWithSubstitutes = (spareParts, substitutesIndex) => {
	if (!substitutesIndex) return spareParts

	return spareParts.map((sparePart) => {
		let key = normalizeMaterialCode(sparePart ? sparePart.Code : '')
		let substitute = key.length > 0 ? substitutesIndex[key] : undefined

		return {
			...sparePart,
			IsDiscontinued: !!substitute,
			Substitute: substitute ? substitute : null
		}
	})
}

/**
 * Action to serch by multitple query to parts
 */
export const sparePartsSAPSelector = createSelector(
	(state) => state.spareParts.getIn(['show', 'equipmentsSap', 'SpareParts']),
	(state) => state.spareParts.getIn(['show', 'querySparePart']),
	substituteMaterialsIndexSelector,
	(sparePartsSAP, query, substitutesIndex) => {
		const getFilteredSpareParts = (spareParts) => {
			if (query && sparePartsSAP) {
				var listSearch = query.split('*')
				var filtered = listSearch.filter((v) => v !== '' && v !== ' ')
				let serchRegexp = ''
				filtered.map((querySearch) => {
					return (serchRegexp += `(?=.*${querySearch.trim().toLowerCase()})`)
				})

				// Search with match regexp
				return spareParts.filter((sparePart) => {
					return (
						sparePart.Code.trim().match(serchRegexp) ||
						(sparePart.Description &&
							sparePart.Description.trim()
								.toLowerCase()
								.match(serchRegexp)) ||
						(sparePart.DescriptionMat &&
							sparePart.DescriptionMat.trim()
								.toLowerCase()
								.match(serchRegexp)) ||
						(sparePart.Material && sparePart.Material.trim().match(serchRegexp))
					)
				})
			} else return spareParts
		}
		let result = sparePartsSAP ? toSafeArray(sparePartsSAP) : []
		result = getFilteredSpareParts(result)
		result = decorateWithSubstitutes(result, substitutesIndex)
		return result
	}
)
